import axios, { AxiosResponse } from 'axios';
import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { mockResponse } from 'api/use-api-query';
import { useApiMutation } from 'api/use-api-mutation';
import { AnnotationsApi } from 'oas/internal';

export interface AnnotationCategoryRequest {
    source_name: string;
    import_name: string;
    start_offset: number;
    end_offset: number;
    default_color: string;
    is_bakeable: number;
    is_event: number;
}

export interface AnnotationCategory {
    id: string;
    source_name: string;
    import_name: string;
    start_offset: number;
    end_offset: number;
    default_color: string;
    is_bakeable: number;
    is_event: number;
    created_at: string;
    modified_at: string;
}

export interface AnnotationLayerDeleteRequest {
    annotationLayerId: string;
}

export const useCreateAnnotationCategoriesBatch = (): UseMutationResult<
    AnnotationCategory[],
    unknown,
    AnnotationCategoryRequest[]
> => {
    return useMutation({
        mutationFn: async (categoryRequest): Promise<AnnotationCategory[]> => {
            // todo align with releases of annotation service
            if (process.env.REACT_APP_API_HOST?.includes('localhost')) {
                const response: AxiosResponse<AnnotationCategory[]> = await axios.post(
                    'http://localhost:8071/annotation_categories/batch?customer_id=2591',
                    categoryRequest,
                );
                return response.data;
            }
            // return mocked data unless develop env
            return categoryRequest.map((req) => ({
                id: req.source_name,
                ...req,
                created_at: Date.now().toString(),
                modified_at: Date.now().toString(),
            }));
        },
    });
};

export const useDeleteAnnotationLayer = (): UseMutationResult<
    AxiosResponse<unknown>,
    unknown,
    AnnotationLayerDeleteRequest
> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async () => {
            const response = await mockResponse({});
            return { ...response, status: 204, statusText: 'No Content' };
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotation-layers'],
                });
            },
        },
    );
};

export const useDeleteAnnotation = (): UseMutationResult<void, unknown, string, unknown> => {
    const queryClient = useQueryClient();
    return useApiMutation(
        async (id, apiConfig, axiosInstance) => {
            await new AnnotationsApi(apiConfig, '', axiosInstance).deleteAnnotation(id);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['annotation'],
                });
            },
        },
    );
};
